/**
 * HTTP-клиент для сайта rublox.pro.
 *
 * Своего бэкенда у сайта НЕТ — переиспользуем существующие микросервисы
 * онлайн-школы «Майнкрафтия» через Nginx Proxy Manager:
 *   - api-user   (/api-user/...)   — аккаунты, алмазы, рейтинг
 *   - api-storys (/api-storys/...) — фид игр Рублокса
 *
 * Особенность авторизации (см. reference_rublox_kotlin_api):
 * заголовок Authorization несёт СЫРОЙ JWT-токен БЕЗ префикса "Bearer".
 */

// В dev (localhost) — пустой base: запросы идут на тот же origin (localhost:3001),
// а src/setupProxy.js проксирует /api-* на прод minecraftia-school.ru (обход CORS).
// В проде (rublox.pro) — абсолютный base, запросы через NPM напрямую.
export const API_BASE =
  typeof window !== "undefined" && window.location.hostname === "localhost"
    ? ""
    : "https://minecraftia-school.ru";

const TOKEN_KEY = "rublox_token";
const REFRESH_KEY = "rublox_refresh";

/**
 * Событие «сессия окончательно потеряна».
 *
 * Бросается ТОЛЬКО когда сервер явно отверг refresh-токен (401/403), то есть
 * войти заново придётся руками. На 5xx и обрыв сети НЕ бросается — иначе
 * каждый деплой бэкенда разлогинивал бы всех, кто в этот момент на сайте.
 *
 * Через DOM-событие, а не импорт стора: HTTP-слой не должен знать про React
 * и контексты, иначе получается цикл импортов.
 */
export const AUTH_LOST_EVENT = "rublox:auth-lost";

function emitAuthLost() {
  try {
    window.dispatchEvent(new Event(AUTH_LOST_EVENT));
  } catch {
    /* нет window (SSR/тесты) — игнор */
  }
}

/* ── хранилище токена (localStorage) ─────────────────────────────── */

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

export function getRefreshToken() {
  try {
    return localStorage.getItem(REFRESH_KEY) || null;
  } catch {
    return null;
  }
}

export function setTokens(token, refreshToken) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  } catch {
    /* приватный режим браузера — молча игнорируем */
  }
}

export function clearTokens() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* игнор */
  }
}

/** Есть ли чем продлевать сессию. Смотрим на refresh, а не на access:
 *  access мог протухнуть, но сессия жива, пока цел refresh. */
export function hasSession() {
  return Boolean(getRefreshToken() || getToken());
}

/* ── обновление токена ───────────────────────────────────────────── */

const REFRESH_PATH = "/api-user/api/v1/users/auth/refresh";

/**
 * Одна попытка обновления. Возвращает true/false, наружу не бросает.
 *
 * Различаем два принципиально разных случая:
 *   401/403 — сервер СКАЗАЛ, что токен мёртв → чистим и сообщаем приложению;
 *   всё остальное (5xx, 404, обрыв сети, деплой) — временная беда,
 *   токены НЕ трогаем, попробуем при следующем действии.
 */
async function doRefresh() {
  const rt = getRefreshToken();
  if (!rt) return false;

  /* Обновляем токен ТОЙ версией, которой он выдан.
     Раньше здесь безусловно вызывался refresh v1. После переезда на v2
     это ломало живую сессию: v1 не знает refresh-токен v2, отвечает 401,
     а код на 401 чистит токены и шлёт «сессия потеряна» — человека
     выбрасывало на лендинг, хотя войти он только что успел.
     Формат тела тоже разный: v1 ждёт {refreshToken}, v2 — {refresh_token}. */
  const useV2 = (process.env.REACT_APP_IDENTITY || "v1").toLowerCase() === "v2";
  const url = useV2
    ? `${API_BASE}/api/v2/identity/auth/refresh`
    : API_BASE + REFRESH_PATH;
  const body = useV2 ? { refresh_token: rt } : { refreshToken: rt };

  let res;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    return false; // сеть/недоступность — сессию не убиваем
  }

  if (res.status === 401 || res.status === 403) {
    clearTokens();
    emitAuthLost();
    return false;
  }
  if (!res.ok) return false;

  const data = await res.json().catch(() => null);
  // v2 отдаёт access_token/refresh_token, v1 — token/jwt + refreshToken.
  const token = data?.access_token || data?.token || data?.jwt;
  if (!token) return false;

  /* Обе версии ротируют refresh: старый гасится, выдаётся новый —
     значит его обязательно надо сохранить, иначе СЛЕДУЮЩЕЕ обновление
     уйдёт с уже погашенным токеном и человека разлогинит.
     Имена полей разные: v1 — refreshToken, v2 — refresh_token.
     Если сервер новый не прислал, оставляем текущий (`rt`), а не
     затираем значение undefined'ом. */
  setTokens(token, data?.refresh_token || data?.refreshToken || rt);
  return true;
}

let refreshing = null;

/**
 * Single-flight: все параллельные вызовы ждут ОДИН и тот же запрос.
 *
 * Критично именно для v1: refresh-токен одноразовый и ротируется
 * (`redis_client.delete(f"refresh:{refresh}")` в Auth.py). Две гонки =
 * второй запрос уходит с уже удалённым токеном и получает 401 → сессия
 * умирает на ровном месте. На сайте параллельные запросы обычны
 * (профиль + алмазы + лента разом), так что это не теория.
 */
export function refreshOnce() {
  if (!refreshing) {
    refreshing = doRefresh().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

/** Срок жизни access-токена (exp) в миллисекундах, 0 — если нет или битый. */
function accessExpMs() {
  const t = getToken();
  if (!t) return 0;
  try {
    const part = t.split(".")[1];
    if (!part) return 0;
    // base64url → base64, добиваем padding
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "===".slice((b64.length + 3) % 4);
    const payload = JSON.parse(decodeURIComponent(escape(atob(padded))));
    return typeof payload?.exp === "number" ? payload.exp * 1000 : 0;
  } catch {
    return 0;
  }
}

/**
 * Обновить access заранее, если он вот-вот истечёт.
 *
 * Зовём на старте приложения, при возврате на вкладку и по таймеру. Смысл:
 * не дожидаться 401 посреди действия пользователя, а подстелить соломки
 * заранее. Возвращает true, если токен пригоден для запросов.
 */
export async function ensureFreshAccess() {
  if (!getRefreshToken()) return Boolean(getToken());
  const exp = accessExpMs();
  // Запас в минуту: между проверкой и запросом токен не должен успеть умереть.
  if (exp && exp - Date.now() > 60_000) return true;
  return refreshOnce();
}

/* ── базовый запрос ──────────────────────────────────────────────── */

/**
 * Ошибка API с человеко-читаемым сообщением на русском.
 */
export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/**
 * Низкоуровневый fetch к микросервисам.
 * @param {string} path     — путь, начиная с "/api-user/..." или "/api-storys/..."
 * @param {object} options  — { method, body, auth, signal }
 *   auth=true — добавить заголовок Authorization с сырым токеном.
 */
export async function request(path, options = {}) {
  const { method = "GET", body, auth = false, signal, _retried = false } = options;

  const headers = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (auth) {
    const token = getToken();
    if (token) headers["Authorization"] = token; // СЫРОЙ токен, без "Bearer"
  }

  let res;
  try {
    res = await fetch(API_BASE + path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (e) {
    if (e.name === "AbortError") throw e;
    throw new ApiError("Не удалось связаться с сервером. Проверьте интернет.", 0);
  }

  // Access протух — обновляем токен и повторяем запрос ОДИН раз.
  // Флаг _retried защищает от бесконечного цикла, если сервер отдаёт 401
  // и на свежий токен (например, доступ реально запрещён).
  if (res.status === 401 && auth && !_retried && getRefreshToken()) {
    const ok = await refreshOnce();
    if (ok) {
      return request(path, { ...options, _retried: true });
    }
  }

  // Пытаемся разобрать тело как JSON (даже у ошибок оно обычно есть).
  let data = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }

  if (!res.ok) {
    // Бэкенд иногда шлёт сырые error-коды (email_not_confirmed,
    // project_not_published и т.п.). Переводим в человеко-читаемое
    // сообщение. Если у поля error известный код — используем его перевод,
    // иначе берём message/detail, иначе дефолт по статусу.
    const errCode = data?.error;
    const friendly = errCode ? ERROR_MESSAGES[errCode] : null;
    const msg =
      friendly ||
      (data && (data.message || data.detail)) ||
      (data && data.error) ||
      defaultErrorMessage(res.status);
    const err = new ApiError(msg, res.status);
    err.code = errCode || null;
    // Прокидываем captcha_required-флаг из тела (бэк шлёт его при адаптивной
    // капче). Без этого фронту приходится угадывать по тексту "картинки".
    err.captchaRequired = Boolean(data && data.captcha_required);
    throw err;
  }

  return data;
}

const ERROR_MESSAGES = {
  email_not_confirmed:
    "Подтверди email в личном кабинете — без этого нельзя оставлять комментарии.",
  project_not_published:
    "Игра не опубликована — комментировать можно только опубликованные плейсы.",
  comment_banned:
    "Ты временно не можешь оставлять комментарии (блокировка за спам).",
  too_frequent:
    "Комментарии можно оставлять не чаще раза в 5 минут.",
  blocked_text:
    "В комментарии есть запрещённые слова. Убери их и попробуй снова.",
  invalid_text:
    "Текст комментария некорректен.",
  comment_too_long:
    "Комментарий слишком длинный (максимум 1000 символов).",
  spam_duplicate:
    "Этот текст уже отправлен на другие плейсы — выглядит как спам.",
  too_fast:
    "Комментировать можно не чаще раза в 3 минуты.",
  empty:
    "Комментарий не может быть пустым.",
  too_short:
    "Слишком короткий комментарий.",
  too_long:
    "Комментарий слишком длинный (максимум 2000 символов).",
  text_rejected:
    "В комментарии есть запрещённые слова. Убери их и попробуй снова.",
};

function defaultErrorMessage(status) {
  switch (status) {
    case 400:
      return "Неверный запрос. Проверьте введённые данные.";
    case 401:
      return "Сессия истекла. Войди заново.";
    case 403:
      return "Доступ запрещён.";
    case 404:
      return "Ресурс не найден.";
    case 409:
      return "Такой аккаунт уже существует.";
    case 429:
      return "Слишком много запросов. Подожди немного.";
    case 500:
    case 502:
    case 503:
      return "Сервер временно недоступен. Попробуйте позже.";
    default:
      return "Произошла ошибка. Попробуйте ещё раз.";
  }
}
